import React, { useEffect, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Eye } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const COLORS = ["#002FA7", "#B2A475", "#10b981", "#ef4444", "#8b5cf6", "#f59e0b", "#0ea5e9", "#ec4899"];

const AdminStatsDialog = ({ restaurant, onClose }) => {
  const [period, setPeriod] = useState("day");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!restaurant) return;
    setLoading(true);
    adminApi.get(`/admin/restaurants/${restaurant.id}/analytics`, { params: { period } })
      .then((r) => setData(r.data))
      .catch(() => setData({ labels: [], menus: [] }))
      .finally(() => setLoading(false));
  }, [restaurant, period]);

  const chartData = data ? (data.labels || []).map((label, i) => {
    const row = { label };
    (data.menus || []).forEach((m) => { row[m.name] = m.data[i] || 0; });
    return row;
  }) : [];

  const grandTotal = data ? (data.menus || []).reduce((s, m) => s + (m.total || 0), 0) : 0;

  return (
    <Dialog open={!!restaurant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-stats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-[#002FA7]" /> Statistiche · {restaurant?.name}</DialogTitle>
          <DialogDescription>Visualizzazioni dei menù nel tempo. Totale storico: <b>{grandTotal}</b>.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-2">
          <button onClick={() => setPeriod("day")} data-testid="stats-period-day" className={`rounded-full px-4 py-1.5 text-sm font-semibold border ${period === "day" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border text-muted-foreground hover:border-[#002FA7]"}`}>Giornaliero (30 gg)</button>
          <button onClick={() => setPeriod("week")} data-testid="stats-period-week" className={`rounded-full px-4 py-1.5 text-sm font-semibold border ${period === "week" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border text-muted-foreground hover:border-[#002FA7]"}`}>Settimanale (12 sett)</button>
        </div>

        {loading ? (
          <div className="h-72 flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento…</div>
        ) : grandTotal === 0 ? (
          <div className="h-72 flex items-center justify-center text-muted-foreground text-sm">Ancora nessuna visualizzazione registrata per questo locale.</div>
        ) : (
          <>
            <div className="h-72 w-full" data-testid="stats-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {(data.menus || []).map((m, i) => (
                    <Bar key={m.id} dataKey={m.name} stackId="a" fill={COLORS[i % COLORS.length]} radius={[2, 2, 0, 0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 space-y-2">
              {(data.menus || []).map((m, i) => (
                <div key={m.id} className="flex items-center justify-between text-sm bg-muted/40 rounded-lg px-3 py-2" data-testid={`stats-menu-row-${m.id}`}>
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="truncate font-medium">{m.name}</span>
                  </span>
                  <span className="text-muted-foreground text-xs whitespace-nowrap">
                    <b className="text-foreground">{m.period_total}</b> nel periodo · {m.total} totali
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminStatsDialog;
